import { Title } from '@patternfly/react-core'
import {
  TableComposable,
  TableVariant,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@patternfly/react-table'
import { useCallback } from 'react'
import * as React from 'react'

import type {
  IPatientImportError,
  IPatientImportErrorType,
} from '@app/domain/patient/patient.rest'
import { InfoTooltip } from '@app/utils/info-tooltip-icon'

interface Props {
  errors: IPatientImportError[]
}

export const PatientImportErrorsTable = ({ errors }: Props) => {
  const convertErrorTypeToLabel = useCallback(
    (type: IPatientImportErrorType) => {
      switch (type) {
        case 'duplicate':
          return (
            <>
              Duplicate
              <InfoTooltip content="A patient with the provided MRN already exists in the system." />
            </>
          )
        case 'invalid-birth-sex':
          return (
            <>
              Invalid Birth Sex
              <InfoTooltip content="The provided birth sex is invalid. Valid values are 'male', 'female', 'other', or 'unknown'." />
            </>
          )
        case 'invalid-mrn':
          return (
            <>
              Invalid Medical Record Number (MRN)
              <InfoTooltip content="The provided mrn is invalid. MRN must contain at least one non-whitespace character." />
            </>
          )
        case 'invalid-given-names':
          return (
            <>
              Invalid Given Names
              <InfoTooltip content="The provided Given Names is invalid. Given names must contain at least one non-whitespace character." />
            </>
          )
        case 'invalid-family-name':
          return (
            <>
              Invalid Family Name
              <InfoTooltip content="The provided Family Name is invalid. Family Name must contain at least one non-whitespace character." />
            </>
          )
        case 'invalid-date-of-birth':
          return (
            <>
              Invalid Date of Birth
              <InfoTooltip content="The date of birth is an invalid date." />
            </>
          )
        case 'invalid-phone-type':
          return (
            <>
              Invalid Phone Type
              <InfoTooltip content="The provided phone number type is invalid. Valid values are 'mobile' or 'landline'." />
            </>
          )
        case 'invalid-phone-number':
          return (
            <>
              Invalid Phone Number
              <InfoTooltip content="The provided phone number is not valid. Phone numbers must include the country code." />
            </>
          )
        case 'invalid-email':
          return (
            <>
              Invalid Email
              <InfoTooltip content="The email is invalid." />
            </>
          )
        case 'parsing-error':
          return (
            <>
              Parsing Error
              <InfoTooltip content="The row of the provided file could not be parsed." />
            </>
          )
        case 'failure-to-persist-patient':
        case 'failure-to-persist-contact-information':
          return (
            <>
              Server Error
              <InfoTooltip content="An error occurred while trying to save this record. Please contact your administrator." />
            </>
          )
      }
    },
    [],
  )

  return (
    <>
      <Title headingLevel="h3">Import Errors</Title>

      <TableComposable
        variant={TableVariant.compact}
        aria-label="Import Errors"
      >
        <Thead>
          <Tr>
            <Th>Row</Th>
            <Th>Error</Th>
            <Th>MRN</Th>
            <Th>Given Names</Th>
            <Th>Family Name</Th>
            <Th>Date of Birth</Th>
            <Th>Birth Sex</Th>
            <Th>Email Address</Th>
            <Th>Phone Number Type</Th>
            <Th>Phone Number</Th>
          </Tr>
        </Thead>
        <Tbody>
          {errors.map((e, index) => (
            <Tr key={index}>
              <Td>{e.rowIndex}</Td>
              <Td>{convertErrorTypeToLabel(e.type)}</Td>
              <Td>{e.row?.mrn}</Td>
              <Td>{e.row?.givenNames}</Td>
              <Td>{e.row?.familyName}</Td>
              <Td>{e.row?.dateOfBirth}</Td>
              <Td>{e.row?.birthSex}</Td>
              <Td>{e.row?.emailAddress}</Td>
              <Td>{e.row?.phoneNumberType}</Td>
              <Td>{e.row?.phoneNumber}</Td>
            </Tr>
          ))}
        </Tbody>
      </TableComposable>
    </>
  )
}
